/**
 * A `ChatService` backed by /api/v2, built once and shared by both apps.
 *
 * The shape translation lives here rather than in each app because it is the
 * part that can silently disagree: two hand-written adapters drift, and a drift
 * in this one shows up as messages rendering differently depending on which
 * app you opened.
 *
 * Only the upload flow is injected. It genuinely differs — files-viewer drives
 * a three-step signed-URL upload with progress, the dashboard delegates to its
 * own cloud-files helper — and neither is about the chat API version.
 *
 * WHAT THE TWO APIS DISAGREE ABOUT, and therefore what this maps:
 *
 *   v1                                  v2
 *   ------------------------------      ------------------------------
 *   bare body                           { success, data } envelope
 *   message.id = Monday update id       message.id = UUID
 *   message.html                        message.bodyDoc (TipTap JSON)
 *   whole conversation, no paging       keyset page + nextCursor
 *   assets carry a URL each             assets + a feed-level fileUrls map
 *   POST /chat/message                  POST /chat/messages
 *   POST /chat/message/:id (edit)       PATCH /chat/messages/:id
 */

import type { ChatFeed, ChatMessage, ChatActivity, ChatDoc } from '../types/chat';
import type { ChatService, ChatType, ChatStatusSnapshot, ChatSendResponse } from './chatService';
import type { DocNode } from '../components/DocContent';

/** The subset of an axios instance this needs. Keeps the package HTTP-agnostic. */
export interface ChatHttpClient {
    get<T = unknown>(
        url: string,
        config?: { params?: Record<string, unknown> }
    ): Promise<{
        data: T;
    }>;
    post<T = unknown>(url: string, body?: unknown): Promise<{ data: T }>;
    patch<T = unknown>(url: string, body?: unknown): Promise<{ data: T }>;
    delete<T = unknown>(url: string): Promise<{ data: T }>;
}

export interface ChatServiceV2Deps {
    http: ChatHttpClient;
    /** App-specific upload. Without an `updateId` the file stays unlinked until a body embeds it. */
    uploadFile: (
        patientId: string,
        file: File,
        updateId: string | number | undefined,
        onProgress?: (progress: number) => void,
        chatType?: ChatType
    ) => Promise<{ fileId: string }>;
    onError?: ChatService['onError'];
}

interface V2Asset {
    name: string;
    url: string;
    patientFileId?: string;
}

interface V2Message {
    id: string;
    text: string;
    bodyDoc?: DocNode;
    createdAt: string;
    editedAt?: string;
    parentId?: string;
    author: { name: string; email: string; profilePictureUrl?: string };
    assets: V2Asset[];
}

interface V2Feed {
    conversationId: string | null;
    messages: V2Message[];
    fileUrls: Record<string, string>;
    nextCursor: string | null;
}

/**
 * Tolerate both an enveloped and an already-unwrapped body.
 *
 * The two apps configure their v2 axios client differently: the dashboard
 * unwraps `{ success, data }` in an interceptor, files-viewer does not. Rather
 * than make this factory care which app it is in — and be wrong in exactly one
 * of them — it accepts either.
 */
function unwrap<T>(body: unknown): T {
    if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
        return (body as { data: T }).data;
    }
    return body as T;
}

function chatTypeParams(chatType?: ChatType): Record<string, unknown> {
    // Sent always, unlike v1 where it is omitted for 'external'. v2 validates
    // it with a default, so being explicit costs nothing and reads better in a
    // network log when something is wrong.
    return { chatType: chatType ?? 'external' };
}

/**
 * v2 message → the shape the components already render.
 *
 * `format` is deliberately NOT set to 'html': there is no HTML in a v2 message,
 * and claiming otherwise would send MessageBubble down the HtmlContent branch
 * with an undefined body. `bodyDoc` is what it renders, and `text` remains the
 * plain-text fallback and the thing search matches on.
 */
function toChatMessage(message: V2Message): ChatMessage {
    return {
        id: message.id,
        text: message.text,
        bodyDoc: message.bodyDoc,
        createdAt: message.createdAt,
        editedAt: message.editedAt,
        author: message.author,
        assets: message.assets
    };
}

export function createChatServiceV2(deps: ChatServiceV2Deps): ChatService {
    const { http, uploadFile, onError } = deps;

    return {
        async fetchChat(patientId: string, chatType?: ChatType): Promise<ChatFeed> {
            const response = await http.get<unknown>(`/patients/${patientId}/chat`, {
                params: { ...chatTypeParams(chatType), limit: 200 }
            });
            const feed = unwrap<V2Feed>(response.data);
            return {
                // The port's `itemId` is the patient for external chat. v2 does
                // not deal in board items at all, so this keeps the contract
                // without reintroducing a Monday concept.
                itemId: patientId,
                messages: (feed?.messages ?? []).map(toChatMessage),
                fileUrls: feed?.fileUrls ?? {}
            };
        },

        async fetchChatStatus(patientId: string, chatType?: ChatType): Promise<ChatStatusSnapshot> {
            const response = await http.get<unknown>(`/patients/${patientId}/chat/status`, {
                params: chatTypeParams(chatType)
            });
            const status = unwrap<ChatStatusSnapshot>(response.data);
            return {
                hasUpdate: Boolean(status?.hasUpdate),
                otherSideLastSeenAt: status?.otherSideLastSeenAt ?? null
            };
        },

        async sendMessage(
            patientId: string,
            message: string,
            chatType?: ChatType,
            doc?: ChatDoc
        ): Promise<ChatSendResponse | null> {
            const response = await http.post<unknown>(`/patients/${patientId}/chat/messages`, {
                message,
                bodyDoc: doc,
                ...chatTypeParams(chatType)
            });
            const sent = unwrap<{ messageId: string }>(response.data);
            // The UUID, not the Monday id: attachments are linked to the row,
            // and the Monday id is provenance the server owns and will drop.
            return sent?.messageId ? { updateId: sent.messageId } : null;
        },

        async editMessage(
            patientId: string,
            messageId: string,
            message: string,
            doc?: ChatDoc
        ): Promise<unknown> {
            const response = await http.patch<unknown>(
                `/patients/${patientId}/chat/messages/${messageId}`,
                { message, bodyDoc: doc }
            );
            return unwrap(response.data);
        },

        async deleteMessage(patientId: string, messageId: string): Promise<unknown> {
            const response = await http.delete<unknown>(
                `/patients/${patientId}/chat/messages/${messageId}`
            );
            return unwrap(response.data);
        },

        async deleteMessageFile(
            patientId: string,
            messageId: string,
            patientFileId: string
        ): Promise<void> {
            await http.delete<unknown>(
                `/patients/${patientId}/chat/messages/${messageId}/files/${patientFileId}`
            );
        },

        async fetchActivities(patientId: string, chatType?: ChatType): Promise<ChatActivity[]> {
            const response = await http.get<unknown>(`/patients/${patientId}/chat/activities`, {
                params: chatTypeParams(chatType)
            });
            const body = unwrap<{ activities?: unknown }>(response.data);
            // Checked, not cast: the timeline iterates this, so anything but an
            // array (a Result object reached production once) crashed the chat.
            return Array.isArray(body?.activities) ? (body.activities as ChatActivity[]) : [];
        },

        async uploadInlineImage(
            patientId: string,
            file: File,
            chatType?: ChatType
        ): Promise<{ url: string; fileId: string }> {
            const { fileId } = await uploadFile(patientId, file, undefined, undefined, chatType);
            return { fileId, url: URL.createObjectURL(file) };
        },

        uploadFile,
        onError
    };
}
